import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Bot, Home, Save, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { useSonas } from "../SonasContext";
import ChatPanel from "./ChatPanel";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SharedProjectView = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchProjects } = useSonas();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSharedData = async () => {
      try {
        const { data: proj, error: pErr } = await supabase
          .from('projects')
          .select('*')
          .eq('share_id', shareId)
          .single();

        if (pErr) throw pErr;
        setProject(proj);

        const { data: msgs, error: mErr } = await supabase
          .from('messages')
          .select('*')
          .eq('project_id', proj.id)
          .order('created_at', { ascending: true });

        if (mErr) throw mErr;
        setMessages(msgs.map(m => ({
          id: m.id,
          role: m.role,
          author: m.role === 'user' ? 'Creator' : 'Sonas AI',
          time: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          text: m.content,
          tasks: m.tasks,
        })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedData();
  }, [shareId]);

  const handleSaveToWorkspace = async () => {
    if (!user) {
      toast.error("Please log in to save projects.");
      navigate("/login");
      return;
    }

    setSaving(true);
    try {
      // 1. Create a new project for the user
      const { data: newProj, error: pErr } = await supabase
        .from('projects')
        .insert([{ 
          user_id: user.id, 
          title: `${project.title} (Cloned)`,
          source_share_id: shareId,
          status: 'Stable'
        }])
        .select()
        .single();

      if (pErr) throw pErr;

      // 2. Clone all messages
      const msgsToInsert = messages.map(m => ({
        project_id: newProj.id,
        user_id: user.id,
        role: m.role,
        content: m.text
      }));

      const { error: mErr } = await supabase.from('messages').insert(msgsToInsert);
      if (mErr) throw mErr;

      setSaved(true);
      toast.success("Project saved to your workspace!");
      if (fetchProjects) fetchProjects();
      
      setTimeout(() => {
        navigate(`/app/chat/${newProj.id}`);
      }, 1500);
    } catch (err) {
      toast.error("Failed to save project.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!project) return (
    <div className="sonas-page flex flex-col items-center justify-center gap-4">
      <h1 className="text-2xl font-bold">Project Not Found</h1>
      <Link to="/"><Button>Back Home</Button></Link>
    </div>
  );

  return (
    <div className="sonas-page px-4 py-8 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-8 glass-surface rounded-2xl p-5 border border-white/5">
        <div className="flex items-center gap-3">
          <Bot className="h-8 w-8 text-[var(--sonas-accent-green)]" />
          <div>
            <h1 className="text-xl font-bold">{project.title}</h1>
            <p className="text-xs text-[var(--sonas-text-secondary)]">Shared View · Shared ID: {shareId.slice(0,8)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user && (
            <Button 
              onClick={handleSaveToWorkspace} 
              disabled={saving || saved}
              className={cn(
                "rounded-xl font-bold",
                saved ? "bg-green-500 hover:bg-green-500" : "bg-sonas-accent text-sonas-bg"
              )}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <Check className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {saved ? "Saved" : "Save to Workspace"}
            </Button>
          )}
          <Link to="/">
            <Button variant="ghost" className="rounded-xl border border-[var(--sonas-border)]">
              <Home className="h-4 w-4 mr-2" /> Explore
            </Button>
          </Link>
        </div>
      </header>
      
      <div className="glass-surface rounded-3xl p-6 border border-white/5 min-h-[600px]">
        <ChatPanel 
          messages={messages} 
          isThinking={false} 
          isReadOnly={true}
        />
      </div>
    </div>
  );
};

export default SharedProjectView;